import type { FC } from "react";
import { useRef, useState } from "react";

import classNames from "classnames";
import { noop } from "lodash";

import { SWReactIcons } from "@similarweb/icons";
import {
    Button,
    IconButton,
    Dropdown,
    DropdownButton,
    ChipContainer,
    IconChipItem,
    SimpleChipItem,
} from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { ConditionalTooltip } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/utils";

import { getChipsForBoolean } from "../utilityFunctions";

import {
    DropdownDescription,
    Divider,
    DropdownItem,
    FilterContentContainer,
    FilterContentFooter,
    FilterRuleRow,
    RuleChipsContainer,
    DropdownTitle,
    HeaderTitle,
    BannerText,
    ChipContainerBannerWrapper,
    BannerStyled,
    FilterWrapper,
} from "./styledComponents";
import type {
    DropdownTypeOptionId,
    MultiIncludeExcludePopupContentProps,
    DropdownMatchTypeOptionId,
    IRule,
    IRuleItem,
} from "./types";

/**
@deprecated
instead, use MultiIncludeExcludeFilter from pro-logic -components
 **/
export const MultiIncludeExcludePopupContent: FC<MultiIncludeExcludePopupContentProps> = ({
    maxItemsPerRule,
    maxRules,
    rules,
    fieldType,
    withMatchType = true,
    getRuleSeparatorText = () => translate("multi.include.exclude.keywords.rule.row.separator"),
    withHeader = true,
    withFooter = true,
    children,
    setContainerRef,
    onRowTypeChange,
    onItemSelected,
    deleteRow,
    addRow,
    onRemoveItem,
    onApply,
    onCancelClick,
}) => {
    const DROPDOWN_TYPE_OPTIONS = [
        { id: "Include", title: translate(`multi.include.exclude.${fieldType}.include`) },
        { id: "Exclude", title: translate(`multi.include.exclude.${fieldType}.exclude`) },
    ] as { id: DropdownTypeOptionId; title: string }[];
    const DROPDOWN_MATCH_TYPE_OPTIONS = [
        {
            id: "Phrase",
            title: translate(`multi.include.exclude.${fieldType}.phrase.match.title`),
            description: translate(`multi.include.exclude.${fieldType}.phrase.match.description`),
        },
        {
            id: "Exact",
            title: translate(`multi.include.exclude.${fieldType}.exact.match.title`),
            description: translate(`multi.include.exclude.${fieldType}.exact.match.description`),
        },
    ] as { id: DropdownMatchTypeOptionId; title: string; description: string }[];

    const [currentActiveRow, setCurrentActiveRow] = useState<number>(null);
    const autoCompleteRefs = useRef([]);

    const hasMultipleRules = rules.length > 1;
    const didExceedMaxItems = (rule: IRule) => rule.items.length >= maxItemsPerRule;
    const canAddAnotherRule = rules.every((rule) => rule.items.length > 0);
    const canApply = rules.some((rule) => rule.items.length > 0);

    const getItemsByItemType = (item: IRuleItem[], type: IRuleItem["type"]) => {
        return item.filter((item) => item.type === type).map((item) => item.value);
    };

    const getAutocompleteComponent = (rule: IRule, index: number) => {
        return children({
            rule,
            index,
            isRowActive: currentActiveRow === index,
            isDisabled: didExceedMaxItems(rule),
            onItemSelected,
            getItemsByItemType,
            setAsActiveRow: () => setCurrentActiveRow(index),
            set ref(ref) {
                autoCompleteRefs.current[index] = ref;
            },
            get ref() {
                return autoCompleteRefs.current[index];
            },
        });
    };

    const getRuleChipsComponent = (props) => {
        const Component = props.iconName ? IconChipItem : SimpleChipItem;
        return <Component {...props} />;
    };

    return (
        <FilterWrapper>
            <FilterContentContainer
                ref={(ref) => {
                    setContainerRef(ref);
                }}
            >
                {withHeader ? (
                    <HeaderTitle>{translate(`multi.include.exclude.${fieldType}.header.title`)}</HeaderTitle>
                ) : null}
                {rules.map((rule, index) => (
                    <FilterRuleRow
                        key={index}
                        withDeleteButton={hasMultipleRules}
                        withMatchType={withMatchType}
                        ruleSeparatorText={getRuleSeparatorText(rule, rules[index + 1])}
                    >
                        <Dropdown
                            key={rule.type}
                            width={108}
                            buttonWidth={110}
                            selectedIds={{ [rule.type]: true }}
                            closeOnItemClick
                        >
                            <DropdownButton>
                                <span>
                                    {translate(DROPDOWN_TYPE_OPTIONS.find((item) => item.id === rule.type).title)}
                                </span>
                            </DropdownButton>
                            <>
                                {DROPDOWN_TYPE_OPTIONS.map(({ id, title }) => (
                                    <DropdownItem
                                        key={id}
                                        isSelected={id === rule.type}
                                        onClick={() => onRowTypeChange(index, { type: id })}
                                        padding={"0 16px"}
                                    >
                                        {title}
                                    </DropdownItem>
                                ))}
                            </>
                        </Dropdown>
                        {withMatchType ? (
                            <Dropdown
                                key={rule.matchType}
                                width={318}
                                buttonWidth={140}
                                selectedIds={{ [rule.matchType]: true }}
                                closeOnItemClick
                                cssClassContainer={classNames(
                                    "DropdownContent-container",
                                    "multiIncludeExclude--DropdownContent-container",
                                )}
                            >
                                <DropdownButton>
                                    <span>
                                        {translate(
                                            DROPDOWN_MATCH_TYPE_OPTIONS.find((item) => item.id === rule.matchType)
                                                .title,
                                        )}
                                    </span>
                                </DropdownButton>
                                <>
                                    {DROPDOWN_MATCH_TYPE_OPTIONS.map(({ id, title, description }) => (
                                        <DropdownItem
                                            key={id}
                                            isSelected={id === rule.matchType}
                                            onClick={() => onRowTypeChange(index, { matchType: id })}
                                        >
                                            <DropdownTitle>{title}</DropdownTitle>
                                            <DropdownDescription>{description}</DropdownDescription>
                                        </DropdownItem>
                                    ))}
                                </>
                            </Dropdown>
                        ) : null}
                        <ConditionalTooltip
                            condition={didExceedMaxItems(rule)}
                            content={translate(
                                `multi.include.exclude.${fieldType}.max.${fieldType}.in.rule.tip.tooltip`,
                                { count: maxItemsPerRule },
                            )}
                        >
                            {getAutocompleteComponent(rule, index)}
                        </ConditionalTooltip>
                        {hasMultipleRules ? (
                            <IconButton
                                type="flat"
                                iconName="clear"
                                iconSize="xs"
                                onClick={() => deleteRow(index)}
                            />
                        ) : null}
                        <RuleChipsContainer
                            isEmpty={!rule.items.length}
                            $chipSeparator={translate("multi.include.exclude.keywords.rule.chip.separator")}
                        >
                            <ChipContainer itemsComponent={getRuleChipsComponent}>
                                {[
                                    ...getChipsForBoolean(
                                        getItemsByItemType(rule.items, "keyword"),
                                        "keyword",
                                        noop,
                                        (...args) => onRemoveItem(...args, index),
                                        false,
                                        "keywords",
                                    ),
                                    ...getChipsForBoolean(
                                        getItemsByItemType(rule.items, "keywordList"),
                                        "keywordList",
                                        noop,
                                        (...args) => onRemoveItem(...args, index),
                                        true,
                                        "keyword-group",
                                    ),
                                    ...getItemsByItemType(rule.items, "website").map((item) => ({
                                        text: item,
                                        key: `${item}CHIP`,
                                        onCloseItem: () => onRemoveItem(item, "website", noop, index),
                                    })),
                                ]}
                            </ChipContainer>
                            {didExceedMaxItems(rule) ? (
                                <ChipContainerBannerWrapper>
                                    <BannerStyled
                                        title={
                                            <BannerText>
                                                {translate(
                                                    `multi.include.exclude.${fieldType}.max.${fieldType}.in.rule.tip`,
                                                    { count: maxItemsPerRule },
                                                )}
                                            </BannerText>
                                        }
                                        CustomIcon={
                                            <SWReactIcons
                                                iconName="alerts"
                                                size="sm"
                                            />
                                        }
                                    />
                                </ChipContainerBannerWrapper>
                            ) : null}
                        </RuleChipsContainer>
                    </FilterRuleRow>
                ))}
                <div>
                    {rules.length >= maxRules ? (
                        <BannerStyled
                            title={
                                <BannerText>
                                    {translate(`multi.include.exclude.${fieldType}.max.rules.tip`, {
                                        count: maxRules,
                                    })}
                                </BannerText>
                            }
                            CustomIcon={
                                <SWReactIcons
                                    iconName="alerts"
                                    size="sm"
                                />
                            }
                        />
                    ) : null}
                    <ConditionalTooltip
                        condition={rules.length >= maxRules}
                        content={translate(`multi.include.exclude.${fieldType}.max.rules.tip.tooltip`, {
                            count: maxRules,
                        })}
                    >
                        <IconButton
                            type="flat"
                            iconName="add"
                            iconSize="xs"
                            onClick={addRow}
                            isDisabled={!canAddAnotherRule || rules.length >= maxRules}
                        >
                            {translate(`multi.include.exclude.${fieldType}.add.rule`)}
                        </IconButton>
                    </ConditionalTooltip>
                </div>
            </FilterContentContainer>
            {withFooter ? (
                <>
                    <Divider />
                    <FilterContentFooter>
                        <Button
                            type="flat"
                            onClick={onCancelClick}
                        >
                            {translate(`multi.include.exclude.${fieldType}.cancel`)}
                        </Button>
                        <Button
                            type="primary"
                            isDisabled={!canApply}
                            onClick={onApply}
                        >
                            {translate(`multi.include.exclude.${fieldType}.apply`)}
                        </Button>
                    </FilterContentFooter>
                </>
            ) : null}
        </FilterWrapper>
    );
};
